import { Ty } from '../ty';
import _ from 'lodash';
import { err, ok, Result } from 'neverthrow';
import { Assert } from '@cotera/utilities';
import {
  RelInterfaceShorthand,
  relShorthandInterfaceToFull,
} from './rel-interface';
import { InvalidTypeForAttribute, TypeCheckError } from './type-check-error';
import { TypeConstraint, TypeConstraintItem } from '../constraint/constraints';
import { Constraints } from '../constraint';

/**
 * Checks if a subject type implements a required type.
 *
 * @param params.subject - The type being checked for compatibility
 * @param params.req - The required type that the subject must implement
 *
 * STRICT MODE (default):
 * - Exact field matching required
 * - No extra fields allowed
 * - Maintains backward compatibility
 */
export const implementsTy = (params: {
  subject: Ty.Shorthand;
  req: Ty.Shorthand;
}): boolean => {
  const extendedSubject = Ty.shorthandToTy(params.subject);
  const extendedReqs = Ty.shorthandToTy(params.req);

  if (!extendedReqs.nullable && extendedSubject.nullable) {
    return false;
  }

  if (!extendedReqs.tags.every((tag) => extendedSubject.tags.includes(tag))) {
    return false;
  }

  if (extendedReqs.ty.k === 'primitive') {
    if (extendedSubject.ty.k === 'primitive') {
      if (extendedReqs.ty.t === 'float' && extendedSubject.ty.t === 'int') {
        return true;
      }

      if (isTimeLike(extendedReqs.ty.t) && isTimeLike(extendedSubject.ty.t)) {
        return implsTimeLike({
          subject: extendedSubject.ty.t,
          reqs: extendedReqs.ty.t,
        });
      }

      return extendedSubject.ty.t === extendedReqs.ty.t;
    }

    if (extendedSubject.ty.k === 'range') {
      return implementsTy({
        subject: {
          ty: { k: 'primitive', t: 'int' },
          nullable: extendedSubject.nullable,
          tags: extendedSubject.tags,
        },
        req: extendedReqs,
      });
    }
  }

  if (extendedSubject.ty.k === 'array' && extendedReqs.ty.k === 'array') {
    return implementsTy({
      subject: extendedSubject.ty.t,
      req: extendedReqs.ty.t,
    });
  }

  if (extendedSubject.ty.k === 'struct' && extendedReqs.ty.k === 'struct') {
    // STRUCT MATCHING RULES:
    // 1. All required (non-nullable) fields in req must be present in subject
    // 2. All fields present in subject must match the corresponding fields in req
    // 3. Subject cannot have extra fields that aren't in req
    // 4. Missing fields are allowed only if they are nullable in req

    const subjectFieldEntries = Object.entries(extendedSubject.ty.fields);
    const reqFieldEntries = Object.entries(extendedReqs.ty.fields);

    // Check that all fields in subject exist in req and have compatible types
    for (const [subName, subTy] of subjectFieldEntries) {
      const reqTy = extendedReqs.ty.fields[subName];
      if (!reqTy) {
        // Subject has a field that req doesn't have - not allowed
        return false;
      }

      if (!implementsTy({ subject: subTy, req: reqTy })) {
        // Field type is incompatible
        return false;
      }
    }

    // Check that all non-nullable fields in req are present in subject
    for (const [reqName, reqTy] of reqFieldEntries) {
      const subTy = extendedSubject.ty.fields[reqName];
      if (!subTy) {
        // Field is missing from subject - only allowed if it's nullable in req
        if (!reqTy.nullable) {
          return false;
        }
      }
    }

    return true;
  }

  if (extendedReqs.ty.k === 'range') {
    if (extendedSubject.ty.k === 'range') {
      return (
        extendedReqs.ty.start <= extendedSubject.ty.start &&
        extendedReqs.ty.end >= extendedSubject.ty.end
      );
    }
  }

  if (extendedSubject.ty.k === 'enum') {
    if (extendedReqs.ty.k === 'primitive' && extendedReqs.ty.t === 'string') {
      return true;
    }

    if (extendedReqs.ty.k === 'enum') {
      const allowedVariants = extendedReqs.ty.t;
      return extendedSubject.ty.t.every((variant) =>
        allowedVariants.includes(variant)
      );
    }
  }

  if (extendedReqs.ty.k === 'record') {
    if (extendedSubject.ty.k === 'record') {
      return implementsTy({
        subject: extendedSubject.ty.t,
        req: extendedReqs.ty.t,
      });
    }
    if (extendedSubject.ty.k === 'struct') {
      const req = extendedReqs.ty.t;
      return Object.values(extendedSubject.ty.fields).every((field) =>
        implementsTy({
          subject: field,
          req,
        })
      );
    }
  }

  return false;
};

/**
 * Checks if a subject type is backwards compatible with a required type.
 * This function implements width-increased subtyping for structs where extra fields
 * must be nullable to maintain backwards compatibility.
 *
 * @param original - The original type that the subject must be compatible with
 * @param newType - The new type being checked for backwards compatibility
 * @returns true if the new type is backwards compatible with the original type
 *
 * BACKWARDS COMPATIBILITY RULES:
 * 1. **Width-Increased Subtyping**: A struct with more fields can implement a struct with fewer fields
 * 2. **Nullable Extra Fields**: Any extra fields in the subject must be nullable (nullable: true)
 * 3. **Required Field Matching**: All required fields must still exist and have compatible types
 *
 * Example:
 * - Original: { foo: string, bar: int }
 * - New Type: { foo: string, bar: int, extra: nullable_boolean } ✅ COMPATIBLE
 * - New Type: { foo: string, bar: int, extra: boolean } ❌ NOT COMPATIBLE (extra not nullable)
 */
export const isBackwardsCompatible = (params: {
  original: Ty.Shorthand;
  newType: Ty.Shorthand;
}): boolean => {
  const extendedOriginal = Ty.shorthandToTy(params.original);
  const extendedNewType = Ty.shorthandToTy(params.newType);

  // For backwards compatibility, we need to check if newType can be used in place of original
  // This means newType should implement original (have all required fields with compatible types)
  // AND any extra fields must be nullable

  // First check if newType implements original (ignoring extra fields for now)
  if (extendedOriginal.ty.k === 'struct' && extendedNewType.ty.k === 'struct') {
    // Check that all required fields from original exist in newType and have compatible types
    for (const [reqFieldName, reqFieldTy] of Object.entries(
      extendedOriginal.ty.fields
    )) {
      const newField = extendedNewType.ty.fields[reqFieldName];
      if (!newField) {
        return false; // Required field is missing
      }

      if (!implementsTy({ subject: newField, req: reqFieldTy })) {
        return false; // Field type doesn't implement required type
      }
    }

    // Check that any extra fields in newType are nullable
    for (const [newFieldName, newFieldTy] of Object.entries(
      extendedNewType.ty.fields
    )) {
      if (!(newFieldName in extendedOriginal.ty.fields)) {
        // This is an extra field - it must be nullable
        if (!newFieldTy.nullable) {
          return false; // Extra field is not nullable, violates backwards compatibility
        }
      }
    }

    return true;
  }

  // For non-struct types, use the standard implementsTy check
  return implementsTy({ subject: extendedNewType, req: extendedOriginal });
};

/**
 * Finds the narrowest supertype that can represent all the given types.
 *
 * @param tys - Array of types to find the common supertype for
 *
 * STRUCT UNIFICATION BEHAVIOR:
 * - Struct unification uses strict field matching
 * - Extra fields from different structs are made nullable during unification
 * - This affects how structs are merged when finding common supertypes
 */
export const narrowestSuperTypeOf = (
  tys: readonly Ty.Shorthand[]
): Result<
  Ty.ExtendedAttributeType,
  {
    t: 'incompatible-types';
    lhs: Ty.ExtendedAttributeType;
    rhs: Ty.ExtendedAttributeType;
  }
> => {
  const [first, ...rest] = tys.map((ty) => Ty.shorthandToTy(ty));

  Assert.assert(
    first !== undefined,
    '"narrowestSuperTypeOf" doesnt support empty lists'
  );

  let superType: Ty.ExtendedAttributeType = first;

  outer: for (const ty of rest) {
    for (const [l, r] of [
      [ty, superType],
      [superType, ty],
    ] as const) {
      if (implementsTy({ subject: l, req: r })) {
        superType = r;
        continue outer;
      }

      if (
        implementsTy({
          subject: l,
          req: Ty.makeNullable(r),
        })
      ) {
        superType = Ty.makeNullable(r);
        continue outer;
      }

      if (l.ty.k === 'enum' && r.ty.k === 'enum') {
        const variants: Set<string> = new Set([...l.ty.t, ...r.ty.t]);

        superType = {
          ty: { k: 'enum', t: Object.freeze([...variants].sort()) },
          nullable: l.nullable || r.nullable,
          tags: _.intersection(l.tags, r.tags),
        };
        continue outer;
      }

      if (l.ty.k === 'range' && r.ty.k === 'range') {
        superType = {
          ty: {
            k: 'range',
            t: 'int',
            start: Math.min(l.ty.start, r.ty.start),
            end: Math.max(l.ty.end, r.ty.end),
          },
          nullable: l.nullable || r.nullable,
          tags: _.intersection(l.tags, r.tags),
        };
        continue outer;
      }

      for (const req of ['int', 'string', 'float'] as const) {
        if ([l, r].every((x) => implementsTy({ subject: x, req }))) {
          superType = {
            ty: { k: 'primitive', t: req },
            nullable: l.nullable || r.nullable,
            tags: _.intersection(l.tags, r.tags),
          };
          continue outer;
        }
      }

      if (l.ty.k === 'struct' && r.ty.k === 'struct') {
        const unified: Record<string, Ty.ExtendedAttributeType> = {};

        const uniqueFieldNames = _.uniq(
          [l.ty.fields, r.ty.fields].flatMap((x) => Object.keys(x))
        );

        for (const name of uniqueFieldNames) {
          const leftField = l.ty.fields[name];
          const rightField = r.ty.fields[name];
          const sup = narrowestSuperTypeOf(_.compact([leftField, rightField]));
          if (sup.isOk()) {
            const anyMissing =
              leftField === undefined || rightField === undefined;
            unified[name] = anyMissing ? Ty.makeNullable(sup.value) : sup.value;
          } else {
            return err({
              t: 'incompatible-types',
              lhs: ty,
              rhs: superType,
            });
          }
        }

        superType = {
          ty: { k: 'struct', fields: unified },
          nullable: l.nullable || r.nullable,
          tags: _.intersection(l.tags, r.tags),
        };
        continue outer;
      }
    }

    return err({
      t: 'incompatible-types',
      lhs: ty,
      rhs: superType,
    });
  }

  return ok(Object.freeze(superType));
};

/**
 * Checks if a relation (collection of attributes) implements a required interface.
 *
 * @param params.subject - The relation with attribute types
 * @param params.reqs - The required interface specification
 *
 * TYPE CHECKING BEHAVIOR:
 * - Individual attribute type checking uses strict mode
 * - Struct fields are validated against required types using exact matching
 * - For backwards compatibility checks, use isBackwardsCompatible function
 */
export const implementsRel = (params: {
  subject: { readonly [attr: string]: Ty.Shorthand };
  reqs: RelInterfaceShorthand;
}): Result<true, TypeCheckError> => {
  const fullReqs = relShorthandInterfaceToFull(params.reqs);

  for (const [attrName, { allowed, optional }] of Object.entries(fullReqs)) {
    const subAttr = params.subject[attrName];

    // 1) Check if attribute is missing
    if (subAttr === undefined) {
      if (optional) {
        continue; // It's allowed to be missing
      }
      return err(
        new InvalidTypeForAttribute({
          name: attrName,
          wanted: allowed,
          actual: null,
        })
      );
    }

    // 2) Convert subject to an ExtendedAttributeType
    const subjectTy = Ty.shorthandToTy(subAttr);

    // 3) Convert "allowed" from `'any'|Shorthand[]` into `'any'|AllowedItem[]`
    const newAllowed: TypeConstraint =
      Constraints.convertShorthandArrayToTypeConstraint(allowed);

    // 4) Check if the subject type implements that "Allowed" set
    //    (which is an OR condition among all AllowedItems).
    const checkRes = implementsTypeConstraint(subjectTy, newAllowed);
    if (checkRes.isErr()) {
      return err(
        new InvalidTypeForAttribute({
          name: attrName,
          actual: subjectTy,
          wanted: allowed,
        })
      );
    }
  }

  // If we pass all attributes, it's good
  return ok(true);
};

const TIMESTAMP_HEIRARCHY = ['year', 'month', 'day', 'timestamp'] as const;

const implsTimeLike = (p: {
  subject: Ty.PrimitiveTimeType;
  reqs: Ty.PrimitiveTimeType;
}): boolean =>
  TIMESTAMP_HEIRARCHY.indexOf(p.subject) <= TIMESTAMP_HEIRARCHY.indexOf(p.reqs);

const isTimeLike = (x: Ty.PrimitiveAttributeType): x is Ty.PrimitiveTimeType =>
  (TIMESTAMP_HEIRARCHY as readonly string[]).includes(x);

/**
 * Check if a subject ExtendedAttributeType implements a union (array) of AllowedItem,
 * or 'any'.
 */
export const implementsTypeConstraint = (
  ty: Ty.Shorthand,
  allowed: TypeConstraint
): Result<boolean, TypeCheckError[]> => {
  const subject = Ty.ty(ty);

  if (allowed === 'any') {
    return ok(true);
  }
  // If it's an array of AllowedItem, we check if subject matches at least one
  const results = allowed.map((item) =>
    implementsConstraintItem(subject, item)
  );

  if (results.some((res) => res.isOk())) {
    return ok(true);
  }

  return Result.combineWithAllErrors(results)
    .map((_) => true)
    .mapErr((errors) => errors.flat());
};

/**
 * Checks whether a subject Ty.ExtendedAttributeType "implements" the given AllowedItem (or 'any').
 */
const implementsConstraintItem = (
  subject: Ty.ExtendedAttributeType,
  item: TypeConstraintItem | 'any'
): Result<boolean, TypeCheckError[]> => {
  // If 'any', all subjects are valid
  if (item === 'any') {
    return ok(true);
  }

  const results: Result<boolean, TypeCheckError[]>[] = [];

  switch (item.t) {
    case 'concrete-type': {
      const res = implementsTy({
        subject,
        req: item.ty,
      })
        ? ok(true)
        : err([
            new InvalidTypeForAttribute({
              name: 'concrete-type',
              wanted: [item.ty],
              actual: subject,
            }),
          ]);

      results.push(res);
      break;
    }

    case 'array': {
      if (subject.ty.k !== 'array') {
        results.push(
          err([
            new InvalidTypeForAttribute({
              name: 'array',
              wanted: item.allowed,
              actual: subject,
            }),
          ])
        );
        break;
      }

      results.push(implementsTypeConstraint(subject.ty.t, item.allowed));
      break;
    }

    case 'record': {
      if (subject.ty.k === 'record') {
        const subjectAllowed = subject.ty.t;

        results.push(
          recordImplementsTypeConstraint(subjectAllowed, item.allowed)
        );

        break;
      }

      // If subject is 'struct' and we allow that to match a 'record'
      if (subject.ty.k === 'struct') {
        // We can interpret each field in struct as if it's a "record entry"
        // => each field must be allowed by item.allowed
        const result = Result.combineWithAllErrors(
          Object.values(subject.ty.fields).map((fieldTy) =>
            implementsTypeConstraint(fieldTy, item.allowed)
          )
        )
          .map(() => true)
          .mapErr((errors) => errors.flat());

        results.push(result);

        break;
      }

      // If subject is not a record or struct, it's an error
      results.push(
        err([
          new InvalidTypeForAttribute({
            name: 'record',
            wanted: item.allowed,
            actual: subject,
          }),
        ])
      );
      break;
    }
    case 'struct': {
      if (subject.ty.k !== 'struct') {
        results.push(
          err([
            new InvalidTypeForAttribute({
              name: 'struct',
              wanted: [item],
              actual: subject,
            }),
          ])
        );
        break;
      }
      // Then every field in the required struct must exist in subject
      // (Or possibly the same set of fields, depending on your strictness.)
      const reqFields = item.fields;
      const subFields = subject.ty.fields;

      // Check that for every (reqKey -> reqAllowedItem), the subject has a matching field
      // and that field implements the item:
      for (const [reqKey, reqAllowedItem] of Object.entries(reqFields)) {
        const subFieldTy = subFields[reqKey];

        if (!subFieldTy) {
          // Skip optional fields, report error for required fields
          if (reqAllowedItem.optional) {
            continue;
          }

          // Missing required field
          results.push(
            err([
              new InvalidTypeForAttribute({
                name: reqKey,
                wanted: reqAllowedItem.ty,
                actual: null,
              }),
            ])
          );

          break;
        }
        const res = implementsTypeConstraint(subFieldTy, reqAllowedItem.ty);

        results.push(res);
      }
      // If all fields are satisfied, we say it's good.
      break;
    }

    default:
      // Exhaustiveness. Should never get here if the union is covered.
      return Assert.unreachable(item);
  }

  return Result.combineWithAllErrors(results)
    .map(() => true)
    .mapErr((errors) => errors.flat());
};

const recordImplementsTypeConstraint = (
  subjectAllowed: Ty.ExtendedAttributeType,
  reqAllowed: TypeConstraint
): Result<boolean, TypeCheckError[]> => {
  if (reqAllowed === 'any') {
    return ok(true);
  }

  return Result.combineWithAllErrors(
    reqAllowed.map((reqItem) =>
      implementsConstraintItem(subjectAllowed, reqItem)
    )
  )
    .map(() => true)
    .mapErr((errors) => errors.flat());
};
